import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getContract, generateBid, getBidStatus, saveBid, exportBidUrl, setPipeline, setSubmission, generateActionPlan, uploadItt, deleteItt, getBidProbability, fmtValue, fmtDate, daysUntil } from "@/lib/api";
import { ScoreBadge, Stars } from "@/components/ScoreBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ExternalLink, PenLine, RefreshCw, Copy, Building2, MapPin, Calendar, Tag, Sparkles, FileText, FileType2, Pencil, Save, X, Eye, KanbanSquare, Layers, ListChecks, Send, CheckSquare, Square, AlertTriangle, Rocket, Upload, Trash2, Target, FileCheck2, KeyRound, Mail, ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import { useState, useRef, useCallback, useEffect } from "react";

const PIPELINE_STATUSES = ["Interested", "Bidding", "Submitted", "Won", "Lost"];
const OWNERS = ["Unassigned", "Mark", "Sean"];

const SUBMISSION_STEPS = [
  { id: "portal_registered", label: "Registered on the buyer's e-procurement portal", hint: "Create/activate your supplier account (In-Tend, ProContract, Delta, Jaggaer, Atamis…). 2FA can take a day — do this early." },
  { id: "sq_complete", label: "Selection Questionnaire (SQ/PQQ) completed", hint: "Company details, insurances, financials, accreditations." },
  { id: "questions_answered", label: "All tender questions answered in the portal", hint: "Paste finalised responses into each question box — mind the character/word limits." },
  { id: "bid_exported", label: "Bid response finalised & exported (Word/PDF)", hint: "Use the exports above — check the [placeholders] are all filled." },
  { id: "docs_attached", label: "Supporting documents attached", hint: "Insurances, accreditations, policies (H&S, quality, environmental), case studies, CVs." },
  { id: "pricing_submitted", label: "Pricing / schedule of rates completed", hint: "Enter the pricing schedule exactly in the buyer's template." },
  { id: "signed_off", label: "Final review & sign-off (Mark / Sean)", hint: "Second pair of eyes on compliance and win themes before you commit." },
  { id: "submitted", label: "Submitted before the deadline", hint: "Click submit in the portal — don't leave it to the last hour." },
];


function renderMarkdown(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) =>
    esc(s)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[(.+?)\]/g, '<span class="bg-amber-100 text-amber-900 px-1 font-mono text-[0.85em]">[$1]</span>');

  const lines = md.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Markdown table
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const header = line.split("|").slice(1, -1).map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      out.push(
        `<div class="overflow-x-auto my-3"><table class="w-full text-xs border-2 border-zinc-950 border-collapse">
          <thead><tr>${header.map((h) => `<th class="border border-zinc-300 bg-zinc-950 text-white px-2 py-1.5 text-left font-bold uppercase tracking-wider">${inline(h)}</th>`).join("")}</tr></thead>
          <tbody>${rows.map((r) => `<tr>${r.map((c) => `<td class="border border-zinc-200 px-2 py-1.5 align-top text-zinc-700">${inline(c)}</td>`).join("")}</tr>`).join("")}</tbody>
        </table></div>`
      );
      continue;
    }
    if (/^### /.test(line)) { out.push(`<h3 class="font-display text-lg font-bold mt-5 mb-2 tracking-tight">${inline(line.replace(/^### /, ""))}</h3>`); i++; continue; }
    if (/^## /.test(line)) { out.push(`<h2 class="font-display text-xl font-extrabold mt-6 mb-2 tracking-tight text-[#2F6DA3] border-b-2 border-zinc-950 pb-1">${inline(line.replace(/^## /, ""))}</h2>`); i++; continue; }
    if (/^# /.test(line)) { out.push(`<h1 class="font-display text-2xl font-extrabold mt-6 mb-3">${inline(line.replace(/^# /, ""))}</h1>`); i++; continue; }
    if (/^\s*[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*] /.test(lines[i])) { items.push(`<li class="ml-5 list-disc mb-1">${inline(lines[i].replace(/^\s*[-*] /, ""))}</li>`); i++; }
      out.push(`<ul class="my-2">${items.join("")}</ul>`);
      continue;
    }
    if (/^\s*\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        const m = lines[i].match(/^\s*(\d+)\.\s(.*)/);
        items.push(
          `<div class="flex gap-2 my-1.5"><span class="font-bold text-[#14304F] shrink-0">${m[1]}.</span><span class="flex-1 text-sm leading-relaxed text-zinc-700">${inline(m[2])}</span></div>`
        );
        i++;
      }
      out.push(`<div class="my-2">${items.join("")}</div>`);
      continue;
    }
    if (line.trim() === "" || /^[-—_]{3,}$/.test(line.trim())) { i++; continue; }
    out.push(`<p class="text-sm leading-relaxed text-zinc-700 mb-2">${inline(line)}</p>`);
    i++;
  }
  return out.join("");
}

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [pstatus, setPstatus] = useState("");
  const [powner, setPowner] = useState("Unassigned");
  const [pnotes, setPnotes] = useState("");
  const [psaving, setPsaving] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [submission, setSubmissionState] = useState({});
  const [itt, setItt] = useState(null);
  const [ittUploading, setIttUploading] = useState(false);
  const [prob, setProb] = useState(null);
  const [probLoading, setProbLoading] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const { data: c, isLoading } = useQuery({ queryKey: ["contract", id], queryFn: () => getContract(id) });

  useEffect(() => {
    if (c) {
      setPstatus(c.pipeline_status || "");
      setPowner(c.pipeline_owner || "Unassigned");
      setPnotes(c.pipeline_notes || "");
      setSubmissionState(c.submission_checklist || {});
      setItt(c.itt_filename ? { filename: c.itt_filename, itt_questions: c.itt_questions } : null);
      setProb(c.bid_probability || null);
    }
  }, [c]);

  const handleIttUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIttUploading(true);
    try {
      const res = await uploadItt(id, file);
      setItt({ filename: res.filename, itt_questions: res.itt_questions });
      const n = res.itt_questions?.questions?.length || 0;
      toast.success(n > 0 ? `ITT read — ${n} question${n === 1 ? "" : "s"} found. Regenerate the bid to use them.` : "ITT uploaded. Regenerate the bid to use it.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that document.");
    } finally {
      setIttUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeItt = async () => {
    try {
      await deleteItt(id);
      setItt(null);
      toast.success("ITT removed");
    } catch (err) {
      toast.error("Could not remove the ITT.");
    }
  };

  const assessProb = async (regenerate = false) => {
    setProbLoading(true);
    try {
      const res = await getBidProbability(id, regenerate);
      setProb(res.bid_probability);
    } catch (err) {
      toast.error("Could not assess this opportunity. Try again.");
    } finally {
      setProbLoading(false);
    }
  };

  const toggleSubmission = async (stepId) => {
    const next = !submission[stepId];
    setSubmissionState((prev) => ({ ...prev, [stepId]: next }));
    try {
      await setSubmission(id, stepId, next);
    } catch (e) {
      setSubmissionState((prev) => ({ ...prev, [stepId]: !next }));
      toast.error("Could not save. Try again.");
    }
  };

  const savePipeline = async (nextStatus) => {    const status = nextStatus !== undefined ? nextStatus : pstatus;
    setPsaving(true);
    try {
      await setPipeline(id, { status, owner: powner, notes: pnotes });
      if (nextStatus !== undefined) setPstatus(nextStatus);
      toast.success(status ? `Saved · ${status}` : "Removed from pipeline");
    } catch (e) {
      toast.error("Could not save pipeline");
    } finally {
      setPsaving(false);
    }
  };

  const poll = useCallback(() => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await getBidStatus(id);
        if (res.status === "done" && res.bid_template) {
          clearInterval(pollRef.current);
          setBid(res.bid_template);
          setGenerating(false);
          toast.success("Bid template ready");
        } else if (res.status === "error") {
          clearInterval(pollRef.current);
          setGenerating(false);
          toast.error("Bid generation failed. Try again.");
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
  }, [id]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const startBid = async (regenerate = false) => {
    setGenerating(true);
    if (regenerate) setBid(null);
    try {
      const res = await generateBid(id, regenerate);
      if (res.status === "done" && res.bid_template) {
        setBid(res.bid_template);
        setGenerating(false);
        toast.success(res.cached ? "Loaded saved bid template" : "Bid template ready");
      } else {
        poll();
      }
    } catch (e) {
      setGenerating(false);
      toast.error("Could not start bid generation.");
    }
  };

  if (isLoading) return <div className="text-center py-20 font-mono text-sm text-zinc-400">Loading…</div>;
  if (!c) return <div className="text-center py-20">Contract not found.</div>;

  const currentBid = bid || c.bid_template;
  const currentPlan = plan || c.action_plan;

  const buildPlan = async (regenerate = false) => {
    setPlanning(true);
    if (regenerate) setPlan(null);
    try {
      const res = await generateActionPlan(id, regenerate);
      setPlan(res.action_plan);
      toast.success(res.cached ? "Loaded saved action plan" : "Action plan ready");
    } catch (e) {
      toast.error("Could not build the action plan. Try again.");
    } finally {
      setPlanning(false);
    }
  };

  const copyBid = () => { navigator.clipboard.writeText(currentBid); toast.success("Copied to clipboard"); };

  const startEdit = () => { setDraft(currentBid); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(""); };
  const saveEdit = async () => {
    setSaving(true);
    try {
      await saveBid(id, draft);
      setBid(draft);
      setEditing(false);
      toast.success("Bid saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const exportBid = async (format) => {
    setExporting(format);
    try {
      const resp = await fetch(exportBidUrl(id, format));
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CPH-Bid-${c.title.slice(0, 40).replace(/[^a-z0-9]+/gi, "-")}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Could not export ${format.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/")} data-testid="back-button" className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider hover:text-[#2F6DA3] transition-colors">
        <ArrowLeft size={16} /> Back to Opportunities
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: contract details */}
        <div className="lg:col-span-1 space-y-4">
          <div className="border-2 border-zinc-950 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <ScoreBadge score={c.ai_score} recommendation={c.ai_recommendation} size="lg" />
              <div className="flex items-center gap-2">
                <DeadlineBadge deadline={c.deadline} />
                <Stars score={c.ai_score} />
              </div>
            </div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight leading-tight">{c.title}</h1>
            <div className="mt-4 space-y-2 text-sm">
              <Row icon={Building2} label="Buyer" value={c.buyer_name || "—"} />
              <Row icon={MapPin} label="Region" value={c.region || c.country || "UK"} />
              <Row icon={Calendar} label="Closes" value={fmtDate(c.deadline)} />
              <Row icon={Tag} label="CPV" value={c.cpv_desc || "—"} />
            </div>
            <div className="mt-4 pt-4 border-t-2 border-zinc-100">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400">Estimated Value</div>
              <div className="font-display text-3xl font-extrabold tracking-tight">{fmtValue(c.value_amount)}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.matched_categories?.map((m) => (
                <span key={m} className="text-[10px] font-mono uppercase bg-zinc-100 border border-zinc-200 px-2 py-1 text-zinc-600">{m}</span>
              ))}
            </div>
            {c.url && (
              <a href={c.url} target="_blank" rel="noreferrer" data-testid="external-link" className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#4F9BDC] hover:underline">
                View on {c.source || "source"} <ExternalLink size={13} />
              </a>
            )}
          </div>

          {/* Get the tender documents — portal, reference, buyer contact + steps */}
          <DocsHelp help={c.docs_help} />

          {/* Pipeline tracker */}
          <div className="border-2 border-[#14304F] bg-white p-5" data-testid="pipeline-panel">
            <div className="flex items-center gap-2 mb-3">
              <KanbanSquare size={16} className="text-[#2F6DA3]" />
              <span className="font-display text-base font-bold tracking-tight">Bid Pipeline</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5 mb-3">
              {PIPELINE_STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`pstatus-${s.toLowerCase()}`}
                  onClick={() => savePipeline(pstatus === s ? "" : s)}
                  disabled={psaving}
                  className={`text-[11px] font-bold uppercase tracking-wide border-2 py-1.5 transition-colors ${
                    pstatus === s ? "bg-[#14304F] text-white border-[#14304F]" : "bg-white border-zinc-300 hover:border-[#14304F]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400">Owner</label>
                <Select value={powner} onValueChange={setPowner}>
                  <SelectTrigger data-testid="pipeline-owner" className="border-2 border-zinc-300 h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400">Notes</label>
                <Textarea data-testid="pipeline-notes" value={pnotes} onChange={(e) => setPnotes(e.target.value)}
                  placeholder="Internal notes, next steps…" className="border-2 border-zinc-300 mt-1 text-sm min-h-[70px]" />
              </div>
              <Button onClick={() => savePipeline()} disabled={psaving} data-testid="save-pipeline" className="w-full bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider text-xs">
                {psaving ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Save to Pipeline
              </Button>
            </div>
          </div>

          {/* Ready to submit */}
          {(() => {
            const done = SUBMISSION_STEPS.filter((s) => submission[s.id]).length;
            const total = SUBMISSION_STEPS.length;
            const allDone = done === total;
            const dLeft = daysUntil(c.deadline);
            return (
              <div className="border-2 border-emerald-700 bg-white p-5" data-testid="submission-panel">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Rocket size={16} className="text-emerald-700" />
                    <span className="font-display text-base font-bold tracking-tight">Ready to Submit</span>
                  </div>
                  <span data-testid="submission-progress" className={`text-[11px] font-mono font-bold px-2 py-1 ${allDone ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-600 border border-zinc-300"}`}>
                    {done}/{total}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  Bids are submitted on the buyer's own portal — there's no way to file directly from here. This is your run-up checklist so nothing is missed before the deadline.
                </p>

                {/* Deadline reminder */}
                {dLeft !== null && (
                  <div className={`text-xs px-3 py-2 mb-3 flex items-center gap-2 border ${dLeft < 5 ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                    <AlertTriangle size={13} className="shrink-0" />
                    <span>{dLeft < 0 ? "Deadline has passed" : dLeft === 0 ? "Closes today" : `${dLeft} day${dLeft === 1 ? "" : "s"} until the deadline`} · closes {fmtDate(c.deadline)}</span>
                  </div>
                )}

                {/* Go to portal */}
                {(c.url || (c.notice_links && c.notice_links.length > 0)) && (
                  <a
                    href={c.url || c.notice_links[0]}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="go-to-portal"
                    className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-wider text-xs py-2.5 border-2 border-emerald-700 transition-colors"
                  >
                    <Send size={14} /> Go to submission portal <ExternalLink size={12} />
                  </a>
                )}

                <div className="space-y-1.5" data-testid="submission-checklist">
                  {SUBMISSION_STEPS.map((s) => {
                    const checked = !!submission[s.id];
                    return (
                      <button
                        key={s.id}
                        data-testid={`submission-step-${s.id}`}
                        onClick={() => toggleSubmission(s.id)}
                        className={`w-full text-left flex items-start gap-2.5 p-2.5 border-2 transition-colors ${checked ? "bg-emerald-50 border-emerald-300" : "bg-white border-zinc-200 hover:border-emerald-400"}`}
                      >
                        {checked ? <CheckSquare size={16} className="text-emerald-700 shrink-0 mt-0.5" /> : <Square size={16} className="text-zinc-300 shrink-0 mt-0.5" />}
                        <span>
                          <span className={`block text-xs font-bold leading-snug ${checked ? "text-emerald-900 line-through" : "text-zinc-800"}`}>{s.label}</span>
                          <span className="block text-[11px] text-zinc-500 mt-0.5 leading-snug">{s.hint}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {allDone && (
                  <div className="mt-3 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 px-3 py-2 flex items-center gap-2" data-testid="submission-complete">
                    <CheckSquare size={14} /> All set — submit in the portal and mark this contract "Submitted" in the pipeline.
                  </div>
                )}
              </div>
            );
          })()}

          <div className="border-2 border-zinc-200 bg-white p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#2F6DA3]">AI Assessment</div>
              {c.opportunity_type === "Possible" ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#4F9BDC] text-white px-2 py-1">Possible Opportunity</span>
              ) : c.opportunity_type === "Out of Scope" ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-zinc-200 text-zinc-600 px-2 py-1">Out of Scope</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-zinc-900 text-white px-2 py-1">Core Fit</span>
              )}
            </div>
            <p className="text-sm text-zinc-700 leading-relaxed mb-3">{c.ai_summary}</p>
            {c.opportunity_type === "Possible" && c.entry_route ? (
              <div className="text-xs text-[#4F9BDC] bg-blue-50 border border-blue-100 px-3 py-2 mb-3">
                <strong>Route in:</strong> {c.entry_route}
              </div>
            ) : null}
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400 mb-1">Why this score</div>
            <p className="text-sm text-zinc-600 leading-relaxed italic">{c.ai_rationale}</p>
            {c.ai_best_fit && <p className="text-xs text-zinc-500 mt-3"><strong>Best fit:</strong> {c.ai_best_fit}</p>}
          </div>

          {/* Should we bid? — win probability */}
          {(() => {
            const verdictMeta = {
              "Strong chance": { cls: "bg-emerald-600", ring: "#059669", text: "text-emerald-700" },
              "Worth a look": { cls: "bg-[#2F6DA3]", ring: "#2F6DA3", text: "text-[#2F6DA3]" },
              "Long shot": { cls: "bg-zinc-500", ring: "#71717A", text: "text-zinc-500" },
            };
            const m = prob ? (verdictMeta[prob.verdict] || verdictMeta["Worth a look"]) : null;
            return (
              <div className="border-2 border-[#2F6DA3] bg-white p-5" data-testid="bid-probability-panel">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Target size={16} className="text-[#2F6DA3]" />
                    <span className="font-display text-base font-bold tracking-tight">Should we bid?</span>
                  </div>
                  {prob && !probLoading && (
                    <button onClick={() => assessProb(true)} data-testid="redo-probability"
                      className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#2F6DA3] hover:underline">
                      <RefreshCw size={12} /> Redo
                    </button>
                  )}
                </div>
                {!prob && !probLoading && (
                  <div>
                    <p className="text-sm text-zinc-500 mb-3">
                      Get a quick, honest win-probability so you know where to focus effort — based on fit, framework access, local presence and capability gaps.
                    </p>
                    <Button onClick={() => assessProb(false)} data-testid="assess-probability-button"
                      className="w-full bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider text-xs">
                      <Target size={14} className="mr-2" /> Assess win chance
                    </Button>
                  </div>
                )}
                {probLoading && (
                  <div className="flex items-center gap-2 text-sm text-zinc-600 py-4">
                    <RefreshCw size={16} className="animate-spin text-[#2F6DA3]" /> Weighing up your chances…
                  </div>
                )}
                {prob && !probLoading && (
                  <div data-testid="bid-probability-result">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="font-display text-5xl font-extrabold tracking-tighter" style={{ color: m.ring }} data-testid="probability-percent">
                        {prob.percent}<span className="text-2xl">%</span>
                      </div>
                      <div>
                        <span className={`inline-block text-[11px] font-bold uppercase tracking-wider text-white px-2 py-1 ${m.cls}`} data-testid="probability-verdict">
                          {prob.verdict}
                        </span>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-1">Estimated win probability</div>
                      </div>
                    </div>
                    <div className="w-full h-2.5 bg-zinc-100 border border-zinc-200 mb-3">
                      <div className="h-full transition-all" style={{ width: `${prob.percent}%`, background: m.ring }} />
                    </div>
                    {prob.reasons?.length > 0 && (
                      <ul className="space-y-1.5">
                        {prob.reasons.map((r, idx) => (
                          <li key={idx} className="text-xs text-zinc-600 flex items-start gap-2">
                            <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0`} style={{ background: m.ring }} />
                            {r}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="text-[10px] text-zinc-400 mt-3 italic">AI guidance — a starting point, not a guarantee. Always review the full notice.</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Step-by-step action plan */}
          <div className="border-2 border-[#14304F] bg-white p-5" data-testid="action-plan-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <ListChecks size={16} className="text-[#2F6DA3]" />
                <span className="font-display text-base font-bold tracking-tight">How to win — Action Plan</span>
              </div>
              {currentPlan && !planning && (
                <button onClick={() => buildPlan(true)} data-testid="regenerate-plan"
                  className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#2F6DA3] hover:underline">
                  <RefreshCw size={12} /> Redo
                </button>
              )}
            </div>
            {!currentPlan && !planning && (
              <div>
                <p className="text-sm text-zinc-500 mb-3">
                  Get a clear, numbered step-by-step plan — which portal to register on, the documents & accreditations you need, key dates and who does what. No guesswork.
                </p>
                <Button onClick={() => buildPlan(false)} data-testid="build-plan-button"
                  className="w-full bg-[#14304F] hover:bg-[#0d2236] text-white font-bold uppercase tracking-wider text-xs">
                  <ListChecks size={14} className="mr-2" /> Build my action plan
                </Button>
              </div>
            )}
            {planning && (
              <div className="flex items-center gap-2 text-sm text-zinc-600 py-4">
                <RefreshCw size={16} className="animate-spin text-[#2F6DA3]" /> Building your step-by-step plan…
              </div>
            )}
            {currentPlan && !planning && (
              <div data-testid="action-plan-content" className="text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(currentPlan) }} />
            )}
          </div>

          <div className="border-2 border-zinc-200 bg-white p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400 mb-2">Full Notice Description</div>
            <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line max-h-72 overflow-y-auto">{c.description}</p>
          </div>

          {/* Frameworks - how to join */}
          <div className="border-2 border-[#2F6DA3] bg-white p-5" data-testid="frameworks-panel">
            <div className="flex items-center gap-2 mb-3">
              <Layers size={16} className="text-[#2F6DA3]" />
              <span className="font-display text-base font-bold tracking-tight">Framework · How to join</span>
            </div>
            {c.frameworks && c.frameworks.length > 0 ? (
              <div className="space-y-2">
                {c.frameworks.map((f) => (
                  <a key={f.name} href={f.url} target="_blank" rel="noreferrer" data-testid={`framework-link-${f.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    className="block border-2 border-zinc-200 hover:border-[#2F6DA3] p-3 transition-colors group">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm group-hover:text-[#2F6DA3]">{f.name}</span>
                      <ExternalLink size={14} className="text-[#2F6DA3] shrink-0" />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{f.note}</p>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#2F6DA3] mt-1 inline-block">How to join →</span>
                  </a>
                ))}
              </div>
            ) : c.is_framework_self ? (
              <div className="text-sm text-zinc-700">
                <p className="mb-2">This notice <strong>sets up a framework / DPS</strong> — you join it by bidding on this very opportunity.</p>
                {c.url && (
                  <a href={c.url} target="_blank" rel="noreferrer" data-testid="framework-self-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#2F6DA3] hover:underline">
                    Apply via this notice <ExternalLink size={13} />
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-zinc-500">No named framework detected — this looks like a direct contract. Bid via the notice link above. (We auto-detect Procure Partnerships, Fusion21, LHC, Pagabo, CCS, NEPO, Scotland Excel and more.)</p>
            )}
            {c.notice_links && c.notice_links.length > 0 && (
              <div className="mt-3 pt-3 border-t-2 border-zinc-100" data-testid="notice-links">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400 mb-1.5">Registration links found in this notice</div>
                <div className="space-y-1">
                  {c.notice_links.map((u) => (
                    <a key={u} href={u} target="_blank" rel="noreferrer" data-testid="notice-link"
                      className="flex items-center gap-2 text-xs font-bold text-[#2F6DA3] hover:underline break-all">
                      <ExternalLink size={12} className="shrink-0" /> {u}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: bid writer */}
        <div className="lg:col-span-2 space-y-4">
          {/* ITT / tender document upload */}
          <div className="border-2 border-[#2F6DA3] bg-white p-5" data-testid="itt-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FileCheck2 size={16} className="text-[#2F6DA3]" />
                <span className="font-display text-base font-bold tracking-tight">Tender document (ITT / spec)</span>
              </div>
              {itt && (
                <button onClick={removeItt} data-testid="remove-itt"
                  className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-red-600 hover:underline">
                  <Trash2 size={12} /> Remove
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleIttUpload}
              className="hidden"
              data-testid="itt-file-input"
            />
            {!itt ? (
              <div>
                <p className="text-sm text-zinc-500 mb-3">
                  Upload the buyer's actual ITT / specification (PDF or Word). We'll pull out their real questions and scoring weightings so the Bid Writer answers <em>their</em> questions — not generic ones.
                </p>
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={ittUploading}
                  data-testid="upload-itt-button"
                  className="w-full bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider text-xs"
                >
                  {ittUploading ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
                  {ittUploading ? "Reading document…" : "Upload ITT / spec"}
                </Button>
              </div>
            ) : (
              <div data-testid="itt-loaded">
                <div className="flex items-center gap-2 text-sm font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3">
                  <FileText size={14} className="shrink-0" />
                  <span className="truncate">{itt.filename}</span>
                </div>
                {itt.itt_questions?.evaluation_summary && (
                  <p className="text-xs text-zinc-600 mb-2"><strong>Evaluation:</strong> {itt.itt_questions.evaluation_summary}</p>
                )}
                {itt.itt_questions?.questions?.length > 0 ? (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400 mb-1.5">
                      {itt.itt_questions.questions.length} question{itt.itt_questions.questions.length === 1 ? "" : "s"} extracted — the Bid Writer will answer these
                    </div>
                    <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="itt-questions">
                      {itt.itt_questions.questions.map((q, idx) => (
                        <li key={idx} className="text-xs text-zinc-700 flex items-start gap-2">
                          <span className="font-bold text-[#2F6DA3] shrink-0">{q.ref || "•"}</span>
                          <span>
                            {q.question}
                            {(q.weighting || q.word_limit) && (
                              <span className="text-zinc-400"> {q.weighting ? `· ${q.weighting}` : ""}{q.word_limit ? ` · limit ${q.word_limit}` : ""}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500">No structured questions were found, but the document text is attached and will inform the bid. Regenerate the bid to use it.</p>
                )}
                <p className="text-[11px] text-[#2F6DA3] font-bold mt-3">
                  {currentBid ? "↻ Regenerate the bid on the right to answer these questions." : "Now generate the bid on the right — it will answer these questions."}
                </p>
              </div>
            )}
          </div>

          <div className="border-2 border-zinc-950 bg-white min-h-[600px] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#14304F] bg-[#14304F] text-white flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <PenLine size={18} className="text-[#4F9BDC]" />
                <span className="font-display text-lg font-bold tracking-tight">AI Bid Writer</span>
              </div>
              {currentBid && !generating && (
                editing ? (
                  <div className="flex gap-3 items-center">
                    <button onClick={saveEdit} disabled={saving} data-testid="save-bid" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#4F9BDC] hover:text-white transition-colors">
                      {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />} Save
                    </button>
                    <button onClick={cancelEdit} data-testid="cancel-edit" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:text-[#4F9BDC] transition-colors"><X size={13} /> Cancel</button>
                  </div>
                ) : (
                  <div className="flex gap-3 items-center">
                    <button onClick={startEdit} data-testid="edit-bid" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:text-[#4F9BDC] transition-colors"><Pencil size={13} /> Edit</button>
                    <button onClick={copyBid} data-testid="copy-bid" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:text-[#4F9BDC] transition-colors"><Copy size={13} /> Copy</button>
                    <button onClick={() => exportBid("docx")} disabled={exporting} data-testid="export-docx" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:text-[#4F9BDC] transition-colors">
                      {exporting === "docx" ? <RefreshCw size={13} className="animate-spin" /> : <FileText size={13} />} Word
                    </button>
                    <button onClick={() => exportBid("pdf")} disabled={exporting} data-testid="export-pdf" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:text-[#4F9BDC] transition-colors">
                      {exporting === "pdf" ? <RefreshCw size={13} className="animate-spin" /> : <FileType2 size={13} />} PDF
                    </button>
                  </div>
                )
              )}
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
              {!currentBid && !generating && (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="bg-[#2F6DA3] p-3 mb-4"><Sparkles size={24} className="text-white" /></div>
                  <h3 className="font-display text-2xl font-bold mb-2">Generate a winning bid template</h3>
                  <p className="text-sm text-zinc-500 max-w-md mb-6">
                    Produce a detailed, confident, sector-credible bid template tailored to this contract — covering methodology, social value, compliance and key win themes.
                  </p>
                  <Button onClick={() => startBid(false)} data-testid="generate-bid-button" className="bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider px-8 py-6 border-2 border-[#2F6DA3]">
                    <PenLine className="mr-2" size={16} /> Write Bid Template
                  </Button>
                </div>
              )}
              {generating && (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <RefreshCw size={28} className="animate-spin text-[#2F6DA3] mb-4" />
                  <p className="font-display text-xl font-bold">Drafting your bid…</p>
                  <p className="text-sm text-zinc-500 mt-1">Drafting a full submission-grade response (compliance matrix, method statement, STAR case studies, social value KPIs). Sections generate in parallel — usually around 1–2 minutes.</p>
                </div>
              )}
              {currentBid && !generating && editing && (
                <div>
                  <div className="text-xs text-zinc-500 mb-2 flex items-center gap-2">
                    <Pencil size={13} /> Editing — complete the <span className="bg-amber-100 text-amber-900 px-1 font-mono">[placeholders]</span>, then Save and export to Word/PDF.
                  </div>
                  <textarea
                    data-testid="bid-editor"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-full h-[55vh] border-2 border-zinc-300 p-4 font-mono text-xs leading-relaxed focus:border-[#2F6DA3] focus:outline-none"
                    spellCheck
                  />
                </div>
              )}
              {currentBid && !generating && !editing && (
                <div data-testid="bid-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(currentBid) }} />
              )}
            </div>

            {currentBid && !generating && !editing && (
              <div className="px-5 py-3 border-t-2 border-zinc-100 flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Edit the draft, then export to Word or PDF for submission</span>
                <Button onClick={() => startBid(true)} variant="outline" data-testid="regenerate-bid" className="border-2 border-[#14304F] font-bold uppercase tracking-wider text-xs">
                  <RefreshCw className="mr-2" size={14} /> Regenerate
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon size={14} className="text-zinc-400 mt-0.5 shrink-0" />
    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 w-16 shrink-0 mt-0.5">{label}</span>
    <span className="text-zinc-800 flex-1">{value}</span>
  </div>
);

const PORTAL_STEPS = {
  "Public Contracts Scotland (PCS)": ["Register on PCS (supplier) — and PCS-Tender too (separate login)", "Search the reference below on the portal", "Click 'Note Interest' on the notice", "Open the Tender / Documents area to download the ITT"],
  "PCS-Tender (Scotland)": ["Register / log in to PCS-Tender", "Find this opportunity (search reference / buyer)", "Express interest / note interest", "Download the documents from the tender area"],
  "ProContract (Due-North / Proactis)": ["Register / log in to ProContract", "Search for the opportunity (reference or buyer)", "Click 'Register / Express Interest'", "Download the documents from the opportunity"],
  "Delta eSourcing": ["Create a free Delta supplier account", "Find the ITT (search reference / buyer)", "Express interest", "Download the documents"],
  "In-tend": ["Register on the In-tend host named in the notice", "Log in and open the tender", "Express interest", "Download the documents"],
  "Jaggaer / BravoSolution": ["Register on the buyer's Jaggaer instance", "Find the opportunity", "Express interest", "Download the documents"],
  "Atamis (NHS / Health Family)": ["Register on Atamis (Health Family)", "Find the opportunity", "Express interest", "Download the documents"],
  "Find a Tender Service (FTS)": ["Open the notice below", "Find 'How to obtain documents' / the e-sourcing link", "Register on that portal", "Express interest to download the pack"],
  "Contracts Finder": ["Open the notice below", "Check the Documents / Attachments section (may be direct)", "Otherwise follow the buyer's portal link", "Register + express interest to download"],
  "eTendersNI (Due North)": ["Register / log in to eTendersNI", "Find the opportunity", "Express interest", "Download the documents"],
  "Sell2Wales": ["Register on Sell2Wales", "Note interest on the notice", "Download / access the documents"],
  "Constructionline": ["Ensure your Constructionline membership is current", "Complete/refresh your pre-qual profile", "Then follow the buyer's own portal for the ITT"],
};
const GENERIC_STEPS = ["Open the notice below", "Find the e-sourcing / portal link on the notice", "Register on that portal (one-time)", "Express interest to unlock and download the documents"];

const DocsHelp = ({ help }) => {
  const [copied, setCopied] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const contact = help?.buyer_contact || {};
  const ref = help?.reference || "";
  const [subject, setSubject] = useState(`Tender documents request — ${ref}`);
  const [body, setBody] = useState(
    `Hello${contact.name ? " " + contact.name : ""},\n\nPlease could you send us the tender documents for reference ${ref}${help?.source ? ` (${help.source})` : ""}. We would like to express interest and receive the ITT / specification.\n\nMany thanks,\nCPH Retrofit`
  );
  if (!help) return null;
  const portal = help.portal;
  const steps = (portal && PORTAL_STEPS[portal.name]) || GENERIC_STEPS;
  const notRegistered = portal && help.registered === false;
  const mailto = contact.email
    ? `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : null;
  const copyRef = () => { navigator.clipboard?.writeText(ref); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="border-2 border-[#2F6DA3] bg-[#f5f9fc] p-5" data-testid="docs-help-panel">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound size={16} className="text-[#2F6DA3]" />
        <span className="font-display text-base font-bold tracking-tight">Get the tender documents</span>
      </div>

      {notRegistered && (
        <div className="flex items-start gap-2 bg-amber-50 border-2 border-amber-400 p-2.5 mb-3" data-testid="prereg-nudge">
          <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            You're <strong>not registered</strong> on {portal.name} yet — sign up before the deadline so you can access the docs and bid.
          </div>
        </div>
      )}
      {portal && help.registered === true && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 p-2 mb-3 text-xs text-emerald-800 font-semibold" data-testid="prereg-ok">
          <CheckCircle2 size={14} /> You're registered on {portal.name} — just log in and express interest.
        </div>
      )}

      {portal && (
        <div className="mb-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Documents portal</div>
          <div className="font-bold text-sm mb-2">{portal.name} <span className="text-[10px] font-mono uppercase text-zinc-400">· {portal.coverage}</span></div>
          <div className="flex flex-wrap gap-2">
            {help.portal_link && (
              <a href={help.portal_link} target="_blank" rel="noreferrer" data-testid="docs-portal-link" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-[#2F6DA3] text-white px-3 py-1.5 hover:bg-[#234E74]">
                Open the portal <ArrowRight size={13} />
              </a>
            )}
            {notRegistered && (
              <a href={portal.register_url} target="_blank" rel="noreferrer" data-testid="docs-register-link" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider border-2 border-[#2F6DA3] text-[#2F6DA3] px-3 py-1.5 hover:bg-[#2F6DA3] hover:text-white">
                Register on {portal.name.split(" (")[0]} <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
      )}

      {ref && (
        <div className="mb-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Reference to search / quote</div>
          <button onClick={copyRef} data-testid="docs-copy-ref" className="inline-flex items-center gap-2 font-mono text-sm font-bold bg-white border-2 border-zinc-300 px-3 py-1.5 hover:border-[#2F6DA3]">
            {ref} <Copy size={13} className="text-zinc-400" /> {copied && <span className="text-[10px] text-emerald-600">copied</span>}
          </button>
        </div>
      )}

      <div className="mb-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Buyer contact</div>
        {contact.name || contact.email || contact.phone ? (
          <div className="text-sm space-y-1">
            {contact.name && <div className="font-semibold flex items-center gap-1.5"><Building2 size={13} className="text-zinc-400" />{contact.name}</div>}
            {contact.phone && <div className="flex items-center gap-1.5 text-zinc-600"><Phone size={13} className="text-zinc-400" />{contact.phone}</div>}
            {mailto ? (
              <div>
                <button onClick={() => setShowDraft((s) => !s)} data-testid="docs-draft-toggle" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#14304F] text-white px-3 py-1.5 mt-1 hover:bg-black">
                  <Mail size={13} /> {showDraft ? "Hide draft" : "Draft email for the documents"}
                </button>
                {showDraft && (
                  <div className="mt-2 border-2 border-zinc-200 p-2.5 bg-white space-y-2" data-testid="docs-email-draft">
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="docs-email-subject" className="w-full text-xs border border-zinc-300 px-2 py-1 font-semibold" />
                    <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} data-testid="docs-email-body" className="w-full text-xs" />
                    <div className="flex flex-wrap gap-2">
                      <a href={mailto} data-testid="docs-email-send" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#2F6DA3] text-white px-3 py-1.5 hover:bg-[#234E74]">
                        <Send size={12} /> Open in email app
                      </a>
                      <button onClick={() => { navigator.clipboard?.writeText(`To: ${contact.email}\nSubject: ${subject}\n\n${body}`); toast.success("Draft copied to clipboard"); }} data-testid="docs-email-copy" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border-2 border-zinc-300 px-3 py-1.5 hover:border-[#2F6DA3]">
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                    <p className="text-[10px] text-zinc-400">Opens in your own email app so the buyer replies straight to you.</p>
                  </div>
                )}
              </div>
            ) : <div className="text-zinc-500 text-xs">No email on this notice — request docs via the portal.</div>}
          </div>
        ) : (
          <div className="text-xs text-zinc-500">No buyer contact on this notice. Get the docs via the portal above{ref ? `, quoting ${ref}` : ""}. (New scans capture contacts automatically.)</div>
        )}
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1.5">How to get them</div>
        <ol className="space-y-1" data-testid="docs-steps">
          {steps.map((s, i) => (
            <li key={i} className="text-xs text-zinc-700 flex gap-2">
              <span className="font-bold text-[#2F6DA3] shrink-0">{i + 1}.</span> {s}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
